/** Processing, In progress - files that are stored and not yet read (ADR-0212).
 *
 * Three states are here and each has a different next step, so the Action cell is decided by the
 * state and the file, and never by a button that predictably fails (UX-04):
 *
 * - **`validated`, a document**: *Read and redact* - the redaction gate (ADR-0011) is on the act and
 *   it reads the stored object, so the digest covers the text the figures come from.
 * - **`validated`, a spreadsheet**: *Review import*. A file of rows is imported, not read: many rows,
 *   one decision.
 * - **`received` and `extracting`**: no button. Nothing in the product writes either state and no
 *   route moves a document out of it (`resume.WAITING_FOR` answers `re_upload`), so the only way on
 *   is a new upload, which the row says. A button here would read the file, write a redaction entry
 *   and *then* be refused by the state machine.
 *
 * Reading is an Admin's, Approver's or Contributor's act; for anyone else the rows are listed
 * without a button.
 */

import { Hourglass } from "@phosphor-icons/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { useMe } from "../../api/auth";
import { useDocuments } from "../../api/evidence";
import type { DocumentRow } from "../../api/evidence";
import { useDocumentCounts } from "../../api/processing";
import { ReadFailed, WriteFailed } from "../../components/Loaded";
import { EmptyState, EMPTY_FILTERS, FilterLine, GridTable, InfoPopover, StatusChip } from "../../components/ui";
import type { FilterDef, FilterState, GridColumn } from "../../components/ui";
import { isTabularContentType } from "../../lib/documents";
import { kindLabel } from "../../lib/documentKinds";
import { keepSearch } from "../search";
import { stageChipOf } from "../upload/stageChip";
import { MAY_UPLOAD } from "../upload/useSendFiles";
import { useScreen } from "../useScreen";
import { DocumentTiles, FileCell, ProcessingFrame } from "./common";
import styles from "./Processing.module.css";
import { ReadOutcome } from "./ReadOutcome";
import { useReadDocument } from "./useRead";

const LIMIT = 200;

const STAGES: FilterDef = {
  id: "stage",
  label: "Stage",
  mode: "single",
  allLabel: "All stages",
  options: [
    { value: "checked", label: "Checked" },
    { value: "received", label: "Received" },
    { value: "reading", label: "Reading" },
  ],
};

export function InProgress() {
  const screen = useScreen();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const counts = useDocumentCounts();
  const received = useDocuments("received", { oldestFirst: true, limit: LIMIT });
  const validated = useDocuments("validated", { oldestFirst: true, limit: LIMIT });
  const extracting = useDocuments("extracting", { oldestFirst: true, limit: LIMIT });
  const reader = useReadDocument();
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);

  const mayAct = me ? MAY_UPLOAD.includes(me.role) : false;
  const reads = [received, validated, extracting];
  const failure = reads.find((read) => read.error)?.error;
  const pending = reads.some((read) => read.isPending);
  const rows = [received, validated, extracting]
    .flatMap((read) => read.data?.rows ?? [])
    .sort((a, b) => a.id.localeCompare(b.id));
  const total = reads.reduce((sum, read) => sum + (read.data?.total ?? 0), 0);
  const wanted = filters.values.stage?.[0];
  const needle = filters.search.trim().toLowerCase();
  const shown = rows.filter(
    (row) =>
      (!wanted || stageChipOf(row).key === wanted) &&
      (!needle || (row.original_filename ?? "").toLowerCase().includes(needle)),
  );

  // Nothing in flight and nothing filtered away: no filter line and no charts, only the empty state.
  const nothingYet = !pending && !failure && rows.length === 0;

  const columns: GridColumn<DocumentRow>[] = [
    { id: "file", header: "File", cell: (document) => <FileCell document={document} /> },
    { id: "type", header: "Type", cell: (document) => kindLabel(document.kind) },
    {
      id: "site",
      header: "Site (your hint)",
      cell: (document) => document.facility_hint_name ?? <span className={styles.none}>chosen in review</span>,
    },
    {
      id: "stage",
      header: "Stage",
      cell: (document) => {
        const chip = stageChipOf(document);
        return (
          <StatusChip tone={chip.tone} size="sm">
            {chip.word}
          </StatusChip>
        );
      },
    },
    {
      id: "action",
      header: "Action",
      cell: (document) => {
        if (document.state === "received" || document.state === "extracting") {
          // Said, not offered as a button: the only way on is a new upload.
          return (
            <span data-testid="stranded-row" className={styles.actions}>
              <StatusChip tone="warn" size="sm">
                Upload it again
              </StatusChip>
              <Link className={styles.link} to="/upload" search={keepSearch}>
                Upload
              </Link>
            </span>
          );
        }
        if (!mayAct) return <span className={styles.none}>–</span>;
        if (isTabularContentType(document.content_type)) {
          return (
            <button
              className={styles.primary}
              type="button"
              onClick={() =>
                void navigate({
                  to: "/evidence/$documentId/import",
                  params: { documentId: document.id },
                  search: { method: screen.method },
                })
              }
            >
              Review import
            </button>
          );
        }
        const reading = reader.pending && reader.target === document.id;
        return (
          <button
            className={styles.primary}
            type="button"
            disabled={reading}
            onClick={() => void reader.read(document.id)}
          >
            {reading ? "Reading…" : "Read and redact"}
          </button>
        );
      },
    },
  ];

  return (
    <ProcessingFrame
      documentCharts
      tab="In progress"
      nothingYet={nothingYet}
      tiles={
        <DocumentTiles
          counts={counts.data}
          pending={counts.isPending}
          failure={{ error: counts.error, retry: () => void counts.refetch() }}
        />
      }
      filters={
        nothingYet ? undefined : (
          <FilterLine
            filters={[STAGES]}
            state={filters}
            onChange={setFilters}
            searchLabel="Search files in progress"
            resultCount={pending ? undefined : { shown: shown.length, total: Math.max(total, shown.length), noun: "files" }}
            actions={
              <>
                {reader.outcome ? <ReadOutcome outcome={reader.outcome} /> : null}
                <InfoPopover infoKey="processing.in-progress" />
              </>
            }
          />
        )
      }
      table={
        failure ? (
          <ReadFailed
            of="The files in progress"
            error={failure}
            onRetry={() => reads.forEach((read) => void read.refetch())}
          />
        ) : (
          <>
            <GridTable
              caption="Files stored and not yet read"
              testId="in-progress"
              columns={columns}
              rows={shown}
              rowKey={(document) => document.id}
              rowLabel={(document) => document.original_filename ?? "document"}
              loading={pending}
              empty={
                <EmptyState compact icon={Hourglass} title="Nothing is being read" testId="in-progress-empty">
                  Bills you upload wait here while they are read.
                </EmptyState>
              }
            />
            <WriteFailed error={reader.refusal} of="the reading" onReload={reader.clearRefusal} />
          </>
        )
      }
    />
  );
}
